import React from 'react';
import { Logo } from './Logo';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <Logo height={40} />
          </div>
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-[#D4F7DC] transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Cookie Policy</h1>
        <p className="text-gray-400 mb-8">HiBotics AI</p>
        <p className="text-sm text-gray-500 mb-12">Last updated: 30 January 2026</p>

        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 leading-relaxed mb-8">
            This Cookie Policy explains how HiBotics AI ("we", "us", "our") uses cookies and similar technologies when you visit our website at <a href="https://hiboticsai.com" className="text-[#D4F7DC] hover:underline">https://hiboticsai.com</a>.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">1. What Are Cookies?</h2>
            <p className="text-gray-300 leading-relaxed">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help the website remember your preferences and understand how you use the site.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">2. Types of Cookies We Use</h2>
            
            <h3 className="text-lg font-medium text-white mt-6 mb-3">a) Strictly Necessary Cookies</h3>
            <p className="text-gray-300 mb-2">These cookies are essential for the website to function properly. They enable basic features such as:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Page navigation</li>
              <li>Secure areas access</li>
              <li>Form submissions</li>
            </ul>
            <p className="text-gray-400 text-sm mt-2 italic">These cookies cannot be disabled as the website would not function without them.</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">b) Analytics Cookies</h3>
            <p className="text-gray-300 mb-2">These cookies help us understand how visitors interact with our website by collecting anonymous information such as:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>Traffic sources</li>
              <li>Bounce rates</li>
            </ul>
            <p className="text-gray-400 text-sm mt-2 italic">This data helps us improve our website and user experience.</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">c) Functional Cookies</h3>
            <p className="text-gray-300 mb-2">These cookies enable enhanced functionality and personalisation, such as:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Remembering your preferences</li>
              <li>Chat widget functionality</li>
              <li>Form auto-fill features</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">3. Third-Party Cookies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <table className="w-full text-gray-300">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-white">Service</th>
                    <th className="text-left py-2 text-white">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Google Analytics</td>
                    <td className="py-2">Website analytics and traffic analysis</td>
                  </tr>
                  <tr>
                    <td className="py-2">WhatsApp</td>
                    <td className="py-2">Chat functionality</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">4. How Long Do Cookies Last?</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Cookies can be either:</p>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Session cookies</strong> – Deleted when you close your browser</li>
              <li><strong className="text-white">Persistent cookies</strong> – Remain on your device for a set period or until you delete them</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">5. Managing Your Cookie Preferences</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              When you first visit our website, you will see a cookie consent banner allowing you to:
            </p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Accept all cookies</li>
              <li>Accept only necessary cookies</li>
              <li>Customise your cookie preferences</li>
            </ul>
            <p className="text-gray-300 mt-4">
              You can change your preferences at any time by clicking the "Cookie Settings" link in our website footer.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">6. Browser Cookie Controls</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You can also control cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>View cookies stored on your device</li>
              <li>Delete cookies individually or all at once</li>
              <li>Block cookies from specific or all websites</li>
              <li>Set preferences for certain types of cookies</li>
            </ul>
            <p className="text-gray-400 text-sm mt-4 italic">
              Note: Blocking certain cookies may affect your experience on our website.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">7. Updates to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for legal reasons. The latest version will always be available on our website.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">8. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions about our use of cookies, please contact us:
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <p className="text-white font-semibold mb-2">HiBotics AI</p>
              <p className="text-gray-300">
                Email: <a href="mailto:info@hiboticsai.com" className="text-[#D4F7DC] hover:underline">info@hiboticsai.com</a>
              </p>
              <p className="text-gray-300">
                Website: <a href="https://hiboticsai.com" className="text-[#D4F7DC] hover:underline">https://hiboticsai.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} HiBotics AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};
